/* eslint-disable eqeqeq */
import React, { Component } from 'react';
var redirectURL = require('../redirectURL');

const authHeaders = () => ({ headers: { 'Authorization': localStorage.getItem("userid") } });

// Display-only currency formatter (adds thousands separators/₹ symbol, matching the ₹ convention
// already used elsewhere in this codebase, e.g. components/courier/*) - never used for any
// calculation, only for rendering an already-known numeric amount.
function formatINR(amount) {
    if (amount === undefined || amount === null || amount === '') return '-';
    var num = Number(amount);
    if (isNaN(num)) return String(amount);
    try { return '₹' + num.toLocaleString('en-IN'); } catch (e) { return '₹' + num; }
}

// EPOD Phase 4 - AL Review (Action 16) detail + Approve/Reject, embedded in epodScreen.js. Calls
// the exact same backend endpoints alReviewScreen.js's own review modal uses
// (/billing/alReviewDetail, /billing/alReviewAction, /billing/dealerDamageFiles) - no business
// logic is duplicated, only the presentation. Unlike alReviewScreen.js (which opens this modal
// pre-committed to one action via two separate grid buttons), this shows both Approve and Reject
// together and lets the reviewer choose after seeing the dealer's submission - a single entry
// point matching "click a cell, view the data, then decide."
export default class AlReviewActionModal extends Component {
    state = {
        detail: null, detailLoading: true,
        remarks: '', ndvAmount: '',
        submitting: false, error: '',
        damageFiles: [], damageFilesLoading: false,
        // UI-only - gates the Reject button behind a "this is terminal" confirmation step before
        // submit('reject') (the existing, unmodified handler) is ever called. Not sent to/read from
        // the backend and never itself decides eligibility.
        confirmingReject: false,
    }

    componentDidMount() {
        this.loadDetail();
    }

    loadDetail = () => {
        this.setState({ detailLoading: true });
        redirectURL.post("/billing/alReviewDetail", { rowid: this.props.row._id }, authHeaders())
            .then((resp) => {
                if (resp.data && resp.data.status === "success") {
                    var confirmation = resp.data.confirmation;
                    var dealerNdv = confirmation && confirmation.ndv && confirmation.ndv.amount;
                    this.setState({
                        detail: confirmation, detailLoading: false,
                        ndvAmount: (dealerNdv !== undefined && dealerNdv !== null) ? String(dealerNdv) : '',
                    });
                } else {
                    this.setState({ detailLoading: false, error: (resp.data && resp.data.message) || 'Could not load dealer submission.' });
                }
            })
            .catch(() => { this.setState({ detailLoading: false, error: 'Could not load dealer submission.' }); });
    }

    onChangeRemarks = (e) => {
        var v = e.target.value;
        var maxLen = this.props.options.max_remarks_length || 500;
        if (v.length > maxLen) v = v.substring(0, maxLen);
        this.setState({ remarks: v, error: '' });
    }

    onChangeNdvAmount = (e) => {
        this.setState({ ndvAmount: e.target.value, error: '' });
    }

    // Reuses the exact same ownership-checked endpoint the "Damage Files" column on epodScreen.js
    // already uses - no new storage/download mechanism.
    onClickViewDamageFiles = () => {
        if (this.state.damageFilesLoading) return;
        this.setState({ damageFilesLoading: true });
        redirectURL.post("/billing/dealerDamageFiles", { rowid: this.props.row._id }, authHeaders())
            .then((resp) => {
                if (resp.data && resp.data.status === "success") {
                    this.setState({ damageFiles: resp.data.files || [], damageFilesLoading: false });
                } else {
                    this.setState({ damageFilesLoading: false, error: (resp.data && resp.data.message) || 'Could not load damage files.' });
                }
            })
            .catch(() => { this.setState({ damageFilesLoading: false, error: 'Could not load damage files.' }); });
    }

    submit = (action) => {
        var { remarks, ndvAmount, detail } = this.state;
        var options = this.props.options || {};
        var trimmed = (remarks || '').trim();
        if (action === 'reject' && options.require_reject_remarks && !trimmed) {
            this.setState({ error: 'Rejection remarks are required.' });
            return;
        }
        // NDV is dealer-entered; when the dealer's confirmation already has one, it's read-only
        // (see render()) and always valid. The editable fallback only applies to pre-existing
        // confirmations from before dealer-entered NDV existed.
        var dealerHasNdv = detail && detail.ndv && detail.ndv.amount !== undefined && detail.ndv.amount !== null;
        if (action === 'approve' && !dealerHasNdv && options.require_ndv_amount_on_approve
            && (ndvAmount === '' || isNaN(Number(ndvAmount)) || Number(ndvAmount) < 0)) {
            this.setState({ error: 'A valid, non-negative NDV amount is required to approve.' });
            return;
        }
        this.setState({ submitting: true, error: '' });
        redirectURL.post("/billing/alReviewAction", {
            rowid: this.props.row._id, action: action, remarks: trimmed,
            ndv_amount: action === 'approve' && ndvAmount !== '' ? Number(ndvAmount) : undefined,
        }, authHeaders())
            .then((resp) => {
                if (resp.data && resp.data.status === "success") {
                    // action passed through so the parent can tell a successful REJECT apart from a
                    // successful APPROVE - both are a successful API call, but should render as
                    // green vs red alerts respectively.
                    this.props.onSubmitted({ success: true, message: resp.data.message || 'Review recorded.', action: action });
                } else {
                    var failMsg = (resp.data && resp.data.message) || 'Review failed.';
                    // "AL Team is shown the latest value" - on exactly this conflict, immediately
                    // re-pull the dealer's current NDV so the reviewer sees it without closing/reopening.
                    // Every other failure (e.g. "already approved") closes the modal and is reported by
                    // the parent's alert, same as a success.
                    if (resp.data && resp.data.code === 'ndv_value_changed') {
                        this.setState({ submitting: false, error: failMsg });
                        this.loadDetail();
                    } else {
                        this.props.onSubmitted({ success: false, message: failMsg, action: action });
                    }
                }
            })
            .catch((err) => {
                var data = err.response && err.response.data;
                var errMsg = (data && data.message) || 'Review failed.';
                if (data && data.code === 'ndv_value_changed') {
                    this.setState({ submitting: false, error: errMsg });
                    this.loadDetail();
                } else {
                    this.props.onSubmitted({ success: false, message: errMsg, action: action });
                }
            });
    }

    // terminal_rejection is now the org-wide default (see alReviewConfig.js) and rejection was not
    // previously confirmed here at all - this only inserts a "this is terminal" confirmation step in
    // front of the SAME submit('reject') call above; it never calls the API itself and never runs
    // for Approve.
    onClickRejectButton = () => {
        // Same require_reject_remarks condition submit('reject') already enforces below - checked
        // here too only so an empty-remarks Reject doesn't walk the reviewer through the terminal
        // confirmation dialog just to fail immediately after; submit() remains the authoritative
        // check either way.
        var trimmed = (this.state.remarks || '').trim();
        if (this.props.options.require_reject_remarks && !trimmed) {
            this.setState({ error: 'Rejection remarks are required.' });
            return;
        }
        this.setState({ confirmingReject: true, error: '' });
    }
    onCancelRejectConfirm = () => { this.setState({ confirmingReject: false }); }
    onConfirmRejectConfirm = () => { this.setState({ confirmingReject: false }); this.submit('reject'); }

    render() {
        var { row, options, onClose } = this.props;
        var { detail, detailLoading, remarks, ndvAmount, submitting, error, damageFiles, damageFilesLoading, confirmingReject } = this.state;
        var actions = (options && options.approval_actions) || {};
        var dealerHasNdv = detail && detail.ndv && detail.ndv.amount !== undefined && detail.ndv.amount !== null;
        var locked = submitting || confirmingReject;

        return (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
                onClick={locked ? undefined : onClose}>
                <div className="modal-dialog" style={{ maxWidth: 620, width: '100%', margin: 0 }} onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content" style={{ maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column' }}>
                        <div className="modal-header">
                            <div>
                                <h5 className="modal-title mb-0">AL Review - NDV Approval</h5>
                            </div>
                            <button type="button" className="close" aria-label="Close" onClick={onClose} disabled={locked}><span aria-hidden="true">&times;</span></button>
                        </div>
                        <div className="modal-body" style={{ overflowY: 'auto' }}>
                            {/* Submission Details */}
                            <div className="mb-3">
                                <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Submission Details</h6>
                                <div className="row">
                                    <div className="col-6">
                                        <label className="mb-0 text-muted" style={{ fontSize: '12px' }}>GC/Way Bill No</label>
                                        <div style={{ fontWeight: 600 }}>{row.gc_waybill_no}</div>
                                    </div>
                                    <div className="col-6">
                                        <label className="mb-0 text-muted" style={{ fontSize: '12px' }}>Condition</label>
                                        <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{row.dealer_confirmation_condition}</div>
                                    </div>
                                </div>
                            </div>

                            {detailLoading ? <p className="text-muted">Loading dealer's submission...</p> : null}

                            {detail ?
                                <div className="mb-3">
                                    <label className="mb-1 text-muted" style={{ fontSize: '12px' }}>Dealer Remarks</label>
                                    {/* .bg-light in this app's own style.css sets `color: #fff` alongside the
                                        grey background (a global override of Bootstrap's default .bg-light,
                                        which sets no text color) - an inline color here overrides that for
                                        this box only, without touching the shared, app-wide .bg-light rule. */}
                                    <div className="border rounded p-2 bg-light" style={{ fontSize: '13px', color: '#212529', maxHeight: '110px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                        title={(detail.ndv && detail.ndv.remarks) || ''}>
                                        {(detail.ndv && detail.ndv.remarks) || '-'}
                                    </div>
                                    {detail.condition === 'shortage' && detail.ndv && detail.ndv.shortage_quantity !== undefined && detail.ndv.shortage_quantity !== null ?
                                        <div className="mt-2" style={{ fontSize: '13px' }}><b>Shortage quantity:</b> {detail.ndv.shortage_quantity}</div>
                                        : null}
                                    {detail.ndv && detail.ndv.images && detail.ndv.images.length ?
                                        <div className="mt-2" style={{ fontSize: '13px' }}>
                                            <b>Damage files:</b> {detail.ndv.images.length} file(s) on record
                                            {' '}
                                            <button type="button" className="btn btn-sm btn-outline-secondary" style={{ padding: '0px 6px', fontSize: '11px' }}
                                                onClick={this.onClickViewDamageFiles} disabled={damageFilesLoading}>
                                                {damageFilesLoading ? 'Loading...' : 'View / Download'}
                                            </button>
                                            {damageFiles && damageFiles.length ?
                                                <ul className="mb-0 mt-1" style={{ paddingLeft: '18px' }}>
                                                    {damageFiles.map((f, idx) => (
                                                        <li key={idx}>
                                                            {f.url ? <a href={f.url} target="_blank" rel="noopener noreferrer">{f.file_name}</a> : f.file_name + ' (unavailable)'}
                                                        </li>
                                                    ))}
                                                </ul>
                                                : null}
                                        </div>
                                        : null}
                                </div>
                                : null}

                            {/* Dealer Submitted NDV - the focal point of this modal */}
                            <div className="mb-3">
                                {dealerHasNdv ?
                                    <div>
                                        <label className="mb-1 text-muted" style={{ fontSize: '12px' }}>Dealer Submitted NDV</label>
                                        <div className="border rounded text-center py-3" style={{ backgroundColor: '#eef7ee', borderColor: '#c3e6cb' }}>
                                            <div style={{ fontSize: '28px', fontWeight: 700, color: '#1e7e34', lineHeight: 1 }}>{formatINR(detail.ndv.amount)}</div>
                                        </div>
                                        <small className="form-text text-muted">
                                            If the dealer changes this value before you approve, you'll be shown the latest amount and asked to review it again.
                                        </small>
                                    </div>
                                    :
                                    <div className="form-group mb-0">
                                        <label htmlFor="alReviewNdvAmount">NDV Amount (Rs.) {options.require_ndv_amount_on_approve ? <span style={{ color: 'red' }}>*</span> : '(optional)'}</label>
                                        <input id="alReviewNdvAmount" type="number" min="0" className="form-control" value={ndvAmount} onChange={this.onChangeNdvAmount} disabled={locked} />
                                        <small className="form-text text-muted">
                                            This confirmation was submitted before dealer-entered NDV existed - enter the amount to classify it (only needed to Approve).
                                        </small>
                                    </div>
                                }
                            </div>

                            {/* Review Remarks */}
                            <div className="form-group mb-1">
                                <label htmlFor="alReviewRemarksTextarea" className="mb-0">Review Remarks</label>
                                <div className="text-muted mb-1" style={{ fontSize: '12px' }}>
                                    {options.require_reject_remarks ? 'Required when rejecting' : 'Optional'}
                                </div>
                                <textarea id="alReviewRemarksTextarea" className="form-control" rows="3" maxLength={options.max_remarks_length}
                                    value={remarks} onChange={this.onChangeRemarks}
                                    disabled={locked} placeholder="Enter remarks for this decision..." autoFocus />
                            </div>
                            {error ? <div className="alert alert-danger py-1 px-2 mb-0" role="alert">{error}</div> : null}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={locked}>Cancel</button>
                            {actions.reject ?
                                <button type="button" className="btn btn-danger" onClick={this.onClickRejectButton} disabled={locked}>
                                    {submitting ? 'Submitting...' : 'Reject'}
                                </button>
                                : null}
                            {actions.approve ?
                                <button type="button" className="btn btn-success" onClick={() => this.submit('approve')} disabled={locked}>
                                    {submitting ? 'Submitting...' : 'Approve'}
                                </button>
                                : null}
                        </div>
                    </div>
                </div>

                {/* Terminal-rejection confirmation - a UI-only gate in front of the SAME submit('reject')
                    call the Reject button always used; no existing equivalent warning was found
                    anywhere in this modal or in alReviewScreen.js's own review modal. Rendered as a
                    sibling overlay (not nested inside the modal-dialog above) so its z-index sits above
                    the primary modal without being clipped by it. */}
                {confirmingReject ?
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
                        onClick={(e) => e.stopPropagation()}>
                        <div className="modal-dialog" style={{ maxWidth: 420, width: '100%', margin: 0 }}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title mb-0">Confirm Rejection</h5>
                                </div>
                                <div className="modal-body">
                                    <p className="mb-2">Are you sure you want to reject this NDV submission?</p>
                                    <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                                        This rejection will close the current workflow and the dealer will not be able to resubmit this confirmation.
                                    </p>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={this.onCancelRejectConfirm}>Cancel</button>
                                    <button type="button" className="btn btn-danger" onClick={this.onConfirmRejectConfirm}>Confirm Reject</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    : null}
            </div>
        );
    }
}
